import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import AnimateHeight from 'react-animate-height'

import Panel from '../../UniformComponents/Panel'
import DistributeEmptyState from '../../UniformComponents/DistributeEmptyState'
import SearchLayout from '../Layouts/Search'
import DataTable from '../DataTable'
import Typography from '../../UniformComponents/Typography'
import Anchor from '../../UniformComponents/Anchor'
import Tooltip from '../../UniformComponents/Tooltip'
import Checkbox from '../form/Checkbox'
import Button from '../../UniformComponents/Button'
import { PageSwitchTab, tabOptions } from '../PageSwitchTab'
import TemplateSearchStore from './TemplateSearchStore'
import DeleteModal from '../../UniformComponents/DeleteModal'
import PresentationTemplateModal from './PresentationTemplateModal'
import PresentationTemplateEditModal from './PresentationTemplateEditModal'

@observer
class PresentationTemplateSearch extends Component {
    constructor(props) {
        super(props)
        this.store = props.store
        this.templateSearchStore = new TemplateSearchStore(props.store, 'presentation')

        this.templateSearchStore.getTemplates()

        this.state = {
            showFilterPanel: (!!props?.hideFilterAccordion || window.innerWidth > 992),
            showGuideAccordion: !!props.showGuideAtInit,
        }

        this.setColumns()
    }

    componentDidMount() {
        const { store } = this.props
        store.breadcrumps = [
            {
                name: 'presentation templates',
                url: '/presentation-templates',
                title: 'Templates',
            },
        ]
        window.addEventListener('resize', this.checkOverflow)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.checkOverflow)
    }

    checkOverflow = () => {
        const { innerWidth } = window
        if (innerWidth > 992) {
            this.setState({ showFilterPanel: true })
        } else this.setState({ showFilterPanel: !!this.props.hideFilterAccordion || false })
    }

    toggleFilterPanel = () => {
        if (window.innerWidth > 992) return

        this.setState((prevState) => ({
            showFilterPanel: !prevState.showFilterPanel,
        }))
    }

    toggleGuide = () => {
        this.setState((prevState) => ({
            showGuideAccordion: !prevState.showGuideAccordion,
        }))
    }

    handleNewTemplate = () => {
        this.templateSearchStore.openCreatePresentationModal()
    }

    handleEditTemplate = (row) => {
        this.templateSearchStore.openEditPresentationModal(row)
    }

    renderPageTitle = (pageTitle, showFilterPanel) => (
        <div className="heading">
            <div
                className="heading"
                style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                onClick={this.toggleFilterPanel}
            >
                <h3>{pageTitle}</h3>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="filter-icon fa-regular fa-bars-filter" />
                    <p className="filter-text">{i18n.t('report_filters')}</p>
                    <i className={`panel-toggle-icon fa-regular ${showFilterPanel ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </div>
            </div>
        </div>
    )

    renderGuideAccordion = (showGuideAccordion, renderGuide) => (
        <div className="guide-accordion">
            <div
                className="header"
                onClick={this.toggleGuide}
            >
                <i className="fa-solid fa-circle-info" />
                <p>{i18n.t('preview.infoTitle')}</p>
                <i className={`fa-light ${showGuideAccordion ? 'fa-square-chevron-up' : 'fa-square-chevron-down'}`} />
            </div>
            <AnimateHeight
                className="guide-content"
                duration={250}
                height={showGuideAccordion ? 'auto' : 0}
            >
                <hr className="divider" />
                {renderGuide()}
            </AnimateHeight>
        </div>
    )

    setColumns = () => {
        const { templateSearchStore } = this
        const { store } = this
        this.columns = [
            {
                name: i18n.t('templates.templateName'),
                key: 'title',
                cellClass: 'bold',
            },
            {
                name: i18n.t('templates.eventType'),
                key: 'productType',
                dataTransform: (value) => templateSearchStore.getEventType(value),
            },
            {
                name: i18n.t('templates.surveyType'),
                key: 'surveyType',
                dataTransform: (value) => templateSearchStore.getSurveyType(value),
            },
            {
                name: i18n.t('templates.lastUpdated'),
                key: 'updatedAt',
                dataTransform: (value) => (value ? new Date(value).toLocaleDateString() : ''),
            },
            {
                name: '',
                key: 'uuid',
                cellClass: 'overflow',
                dataTransform: (value, row) => (
                    <div className="survey-btn-wrapper">
                        <Anchor
                            cssClass="icon-link-btn"
                            onClick={() => this.handleEditTemplate(row)}
                        >
                            <Tooltip
                                text={i18n.t('templates.edit')}
                                direction="left"
                            />
                            <i className="fa-light fa-edit" />
                        </Anchor>
                        <Anchor
                            cssClass="icon-link-btn"
                            onClick={() => templateSearchStore.clonePresentationTemplate(value)}
                        >
                            <Tooltip
                                text={i18n.t('templates.clone')}
                                direction="left"
                            />
                            <i className="fa-light fa-clone" />
                        </Anchor>
                        {row.createdBy === store.userData.userId && (
                            <Anchor
                                cssClass="icon-link-btn"
                                onClick={() => templateSearchStore.showDeleteTemplateModal(value)}
                            >
                                <Tooltip
                                    text={i18n.t('templates.delete')}
                                    direction="left"
                                />
                                <i className="fa-light fa-circle-xmark" />
                            </Anchor>
                        )}
                    </div>
                ),
            },
        ]
    }

    renderButtons = () => (
        <Button
            cssClass="add-button primary regular"
            onClick={this.handleNewTemplate}
            icon="fa-light fa-circle-plus"
            label={i18n.t('templates.new')}
        />
    )

    renderGuide = () => (
        <div dangerouslySetInnerHTML={{
            __html: i18n.t('templates.introText'),
        }}
        />
    )

    renderTablePanel = () => {
        const { templateSearchStore } = this
        return (
            <DataTable
                store={templateSearchStore}
                inMemory
                dataSet={templateSearchStore.filteredList || []}
                columns={this.columns}
                cssClass="search-table"
            />
        )
    }

    renderFilterOptions = () => {
        const { templateSearchStore } = this
        return (
            <Fragment>
                <br />

                {templateSearchStore.productTypeFilterOptions.length
                    ? (
                        <fieldset className="filter">
                            <Typography type="b">
                                {i18n.t('templates.eventType')}
                            </Typography>

                            <br />

                            <br />

                            {templateSearchStore.productTypeFilterOptions.map((opt) => (
                                <Checkbox
                                    name={opt.value}
                                    label={opt.label}
                                    onBlur={templateSearchStore.changeProductFilter}
                                    checked={templateSearchStore.productFilter.includes(opt.value)}
                                />
                            ))}
                        </fieldset>
                    ) : null}

                {templateSearchStore.surveyTypeFilterOptions.length
                    ? (
                        <fieldset className="filter">
                            <Typography type="b">
                                {i18n.t('templates.surveyType')}
                            </Typography>

                            <br />

                            <br />

                            {templateSearchStore.surveyTypeFilterOptions.map((opt) => (
                                <Checkbox
                                    name={opt.value}
                                    label={opt.label}
                                    onBlur={templateSearchStore.changeSurveyFilter}
                                    checked={templateSearchStore.surveyFilter.includes(opt.value)}
                                />
                            ))}
                        </fieldset>
                    ) : null}

                {templateSearchStore.respondentTypeFilterOptions.length
                    ? (
                        <fieldset className="filter">
                            <Typography type="b">
                                {i18n.t('templates.respondentType')}
                            </Typography>

                            <br />

                            <br />

                            {templateSearchStore.respondentTypeFilterOptions.map((opt) => (
                                <Checkbox
                                    name={opt.value}
                                    label={opt.label}
                                    onBlur={templateSearchStore.changeRespondentFilter}
                                    checked={templateSearchStore.respondentFilter.includes(opt.value)}
                                />
                            ))}
                        </fieldset>
                    ) : null}
            </Fragment>
        )
    }

    render() {
        const { templateSearchStore: { templateList } } = this
        const { store, location, history } = this.props
        return (
            <div>
                <div>
                    <PageSwitchTab
                        appStore={store}
                        location={location}
                        history={history}
                        tabOptions={tabOptions.templates}
                    />
                </div>
                {!templateList.length
                    ? (
                        <div className="search-container has-teal-nav">
                            <div className="left-container">
                                <Panel cssClass="filter-panel overflow-visible no-min-height">
                                    {this.renderPageTitle(i18n.t('templates.title'), this.state.showFilterPanel)}
                                    {this.renderGuideAccordion(this.state.showGuideAccordion, this.renderGuide)}
                                    <AnimateHeight
                                        className="filter-wrapper"
                                        duration={250}
                                        height={this.state.showFilterPanel ? 'auto' : 0}
                                        contentClassName="filter-content"
                                    >
                                        {this.renderFilterOptions()}
                                    </AnimateHeight>
                                </Panel>
                            </div>

                            <div className="right-container">
                                <DistributeEmptyState
                                    title={i18n.t('templates.emptyPresentationStateTitle')}
                                    subtext={i18n.t('templates.emptyPresentationStateSubtext')}
                                    icon="fal fa-presentation-screen"
                                    ctaText={i18n.t('templates.new')}
                                    ctaIcon="fal fa-circle-plus"
                                    onClick={this.handleNewTemplate}
                                />
                            </div>
                        </div>
                    )
                    : (
                        <SearchLayout
                            pageTitle={i18n.t('templates.title')}
                            renderFilterOptions={this.renderFilterOptions}
                            renderTablePanel={this.renderTablePanel}
                            renderGuide={this.renderGuide}
                            renderButtons={this.renderButtons}
                            searchStore={this.templateSearchStore}
                            hasTopNav
                        />
                    )}

                <DeleteModal
                    show={this.templateSearchStore.deleteEmailTemplateModalVisible}
                    onClose={this.templateSearchStore.closeDeleteTemplateModal}
                    onDelete={this.templateSearchStore.deletePresentationTemplate}
                    yesLabel={i18n.t('templates.delete.presentation.yesLabel')}
                    headerText={i18n.t('templates.delete.presentation.title')}
                    content={i18n.t('templates.delete.presentation.description')}
                    noLabel={i18n.t('cancel')}
                />

                <PresentationTemplateModal
                    templateSearchStore={this.templateSearchStore}
                    history={history}
                />
                <PresentationTemplateEditModal
                    templateSearchStore={this.templateSearchStore}
                    history={history}
                />
            </div>
        )
    }
}

export default PresentationTemplateSearch
